import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wallet, PiggyBank, Calculator, BarChart3, Download, LogOut, User,
  Home as HomeIcon, PieChart
} from "lucide-react";
import { LOGO_URL } from "@/config";
import { incomeIcons, expenseIcons, savingsIcons } from "@/data/iconMappings";
import formatCurrency from "@/utils/formatCurrency";
import generatePDF from "@/utils/generatePDF";
import IncomeRow from "@/components/dashboard/IncomeRow";
import IncomeAccordion from "@/components/dashboard/IncomeAccordion";
import ExpenseAccordion from "@/components/dashboard/ExpenseAccordion";
import SavingsRow from "@/components/dashboard/SavingsRow";
import CashFlowSummary from "@/components/dashboard/CashFlowSummary";

const EXPENSE_CATEGORIES = ["Household Expenses", "Lifestyle Expenses", "Dependent Expenses", "Pure Insurance Premiums", "Loan Servicing", "Miscellaneous"];

const CashFlowDashboard = ({ data, onUpdateIncomes, onUpdateExpenses, onUpdateSavings, user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Memoize derived income groupings (recompute only when incomes change)
  const accordionCategories = useMemo(
    () => [...new Set(data.incomes.filter(i => i.subcategory).map(i => i.category))],
    [data.incomes]
  );
  const flatIncomes = useMemo(
    () => data.incomes
      .filter(i => !i.subcategory)
      .map(i => ({ income: i, globalIndex: data.incomes.indexOf(i) })),
    [data.incomes]
  );
  const totalIncomeYearly = useMemo(
    () => data.incomes.reduce((sum, i) => sum + (i.is_monthly ? i.monthly_amount * 12 : i.yearly_amount), 0),
    [data.incomes]
  );
  const totalExpenseYearly = useMemo(
    () => data.expenses.reduce((sum, e) => sum + (e.is_monthly ? e.monthly_amount * 12 : e.yearly_amount), 0),
    [data.expenses]
  );
  const totalSavingYearly = useMemo(
    () => data.savings.reduce((sum, s) => sum + (s.is_monthly ? s.monthly_amount * 12 : s.yearly_amount), 0),
    [data.savings]
  );

  return (
    <div className="finance-app min-h-screen p-4 sm:p-6 lg:p-8" data-testid="cashflow-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header - Mobile Responsive */}
        <div className="flex items-center justify-between mb-6 sm:mb-8 gap-2 sm:gap-4">
          <div className="flex items-center shrink-0">
            <img src={LOGO_URL} alt="SubiWealth" className="h-8 sm:h-10 md:h-12" />
          </div>
          <h1 className="hidden sm:block text-lg sm:text-xl md:text-2xl font-bold text-teal-600 truncate" data-testid="dashboard-title">CashFlow Tracker</h1>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="relative">
              <button 
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex flex-col gap-1 p-2 sm:p-2.5 hover:bg-white/50 rounded-lg transition-colors min-h-[44px] min-w-[44px] items-center justify-center"
                data-testid="hamburger-menu-btn"
              >
                <span className="w-5 h-0.5 bg-teal-700 rounded"></span>
                <span className="w-5 h-0.5 bg-teal-700 rounded"></span>
                <span className="w-5 h-0.5 bg-teal-700 rounded"></span>
              </button>
              
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setMenuOpen(false)}></div>
                  <div className="fixed sm:absolute bottom-0 sm:bottom-auto left-0 right-0 sm:left-auto sm:top-full sm:right-0 sm:mt-2 bg-white rounded-t-2xl sm:rounded-xl shadow-xl border border-gray-200 py-2 z-50 sm:min-w-[220px] max-h-[70vh] overflow-y-auto">
                    <div className="sm:hidden w-12 h-1 bg-gray-300 rounded-full mx-auto mt-2 mb-4"></div>
                    <button data-testid="export-pdf-btn" className="w-full flex items-center gap-3 px-5 py-4 sm:py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left text-base sm:text-sm" onClick={() => { generatePDF(data, user?.name || "User"); setMenuOpen(false); }}>
                      <Download size={20} /> Export PDF
                    </button>
                    <button data-testid="go-to-budget-planner-btn" className="w-full flex items-center gap-3 px-5 py-4 sm:py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left text-base sm:text-sm" onClick={() => { navigate("/budget-planner"); setMenuOpen(false); }}>
                      <PiggyBank size={20} /> Budget Planner
                    </button>
                    <button data-testid="go-to-networth-btn" className="w-full flex items-center gap-3 px-5 py-4 sm:py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left text-base sm:text-sm" onClick={() => { navigate("/net-worth"); setMenuOpen(false); }}>
                      <Wallet size={20} /> Net Worth
                    </button>
                    <button data-testid="go-to-portfolio-btn" className="w-full flex items-center gap-3 px-5 py-4 sm:py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left text-base sm:text-sm" onClick={() => { navigate("/portfolio"); setMenuOpen(false); }}>
                      <PieChart size={20} /> Portfolio
                    </button>
                    <button data-testid="go-to-calculators-btn" className="w-full flex items-center gap-3 px-5 py-4 sm:py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left text-base sm:text-sm" onClick={() => { navigate("/calculators"); setMenuOpen(false); }}>
                      <Calculator size={20} /> Calculators
                    </button>
                    <button data-testid="go-to-analytics-btn" className="w-full flex items-center gap-3 px-5 py-4 sm:py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left text-base sm:text-sm" onClick={() => { navigate("/analytics"); setMenuOpen(false); }}>
                      <BarChart3 size={20} /> Analytics
                    </button>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100">
              <User size={16} className="text-teal-600" />
              <span className="hidden sm:inline text-gray-700 font-medium text-sm">{user?.name}</span>
              <button data-testid="logout-btn" className="ml-1 text-gray-400 hover:text-red-500 transition-colors p-1 min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={onLogout} title="Logout">
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Inflows Section */}
        <div className="finance-card" data-testid="inflows-card">
          <h2 className="section-title">Inflows (Income Statement)</h2>
          <div className="space-y-1">
            {/* Accordion categories (incomes with subcategories) */}
            {accordionCategories.map((category) => (
              <IncomeAccordion key={category} category={category} incomes={data.incomes} onUpdate={onUpdateIncomes} icons={incomeIcons} />
            ))}
            {/* Flat rows (incomes without subcategories) */}
            {flatIncomes.map(({ income, globalIndex }) => (
              <IncomeRow key={income.category} icon={incomeIcons[income.category] || Wallet} label={income.category} income={income} onUpdate={onUpdateIncomes} index={globalIndex} />
            ))}
          </div>
          {/* Total Income */}
          <div className="mt-4 pt-4 border-t-2 border-teal-200" data-testid="total-income-row">
            <div className="flex items-center justify-between px-2 total-row-mobile">
              <span className="text-base sm:text-lg font-bold text-teal-700">Total Income (Yearly)</span>
              <span className="text-xl sm:text-2xl font-bold text-teal-600" data-testid="total-income-value">
                ₹{formatCurrency(totalIncomeYearly)}
              </span>
            </div>
          </div>
        </div>

        {/* Outflows Section */}
        <div className="mt-6">
          <div className="finance-card" data-testid="outflows-card">
            <h2 className="section-title">Outflows (Expense Statement)</h2>
            <div>
              {EXPENSE_CATEGORIES.map((category) => (
                <ExpenseAccordion key={category} category={category} expenses={data.expenses} onUpdate={onUpdateExpenses} icons={expenseIcons} />
              ))}
            </div>
            {/* Total Expense */}
            <div className="mt-4 pt-4 border-t-2 border-red-200" data-testid="total-expense-row">
              <div className="flex items-center justify-between px-2 total-row-mobile">
                <span className="text-base sm:text-lg font-bold text-red-700">Total Expense (Yearly)</span>
                <span className="text-xl sm:text-2xl font-bold text-red-600" data-testid="total-expense-value">
                  ₹{formatCurrency(totalExpenseYearly)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Regular Savings Section */}
        <div className="mt-6">
          <div className="finance-card" data-testid="savings-card">
            <h2 className="section-title">Regular Savings</h2>
            <div className="space-y-1">
              {data.savings.map((saving, index) => (
                <SavingsRow key={saving.category} icon={savingsIcons[saving.category] || PiggyBank} label={saving.category} saving={saving} onUpdate={onUpdateSavings} index={index} />
              ))}
            </div>
            {/* Total Saving */}
            <div className="mt-4 pt-4 border-t-2 border-blue-200" data-testid="total-saving-row">
              <div className="flex items-center justify-between px-2 total-row-mobile">
                <span className="text-base sm:text-lg font-bold text-blue-700">Total Saving (Yearly)</span>
                <span className="text-xl sm:text-2xl font-bold text-blue-600" data-testid="total-saving-value">
                  ₹{formatCurrency(totalSavingYearly)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cash Flow Summary */}
        <div className="mt-6">
          <CashFlowSummary incomes={data.incomes} expenses={data.expenses} savings={data.savings} />
        </div>
      </div>
    </div>
  );
};

export default CashFlowDashboard;
